/**
 * Tournament detail: the management hub for a single tournament.
 */
import { useEffect, useState } from "react";
import type {
  Fixture,
  StandingsRow,
  Team,
  TeamId,
  TournamentDetailResponse,
} from "@sportsos/engine";
import { api, publicTournamentPath } from "../api.ts";
import { AddPlayerSheet } from "./AddPlayerSheet.tsx";

interface Props {
  readonly tournamentId: string;
  readonly onBack: () => void;
  readonly onOpenMatch: (matchId: string) => void;
}

type TabName = "fixtures" | "standings" | "leaderboards";

export function TournamentDetail({ tournamentId, onBack, onOpenMatch }: Props): React.ReactElement {
  const [data, setData] = useState<TournamentDetailResponse | null>(null);
  const [standings, setStandings] = useState<readonly StandingsRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabName>("fixtures");

  const [savedTeams, setSavedTeams] = useState<readonly Team[] | null>(null);
  const [newTeamName, setNewTeamName] = useState("");
  const [addingTeamId, setAddingTeamId] = useState<string | null>(null);
  const [managePlayersTeam, setManagePlayersTeam] = useState<Team | null>(null);
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [generating, setGenerating] = useState(false);

  const loadTournament = async (): Promise<void> => {
    try {
      const detail = await api.getTournament(tournamentId);
      setData(detail);
      if (detail.fixtures.length > 0) {
        const standingsData = await api.getStandings(tournamentId);
        setStandings(standingsData.standings);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load tournament");
    }
  };

  useEffect(() => {
    let live = true;
    void (async () => {
      await loadTournament();
      if (!live) return;
      try {
        const res = await api.listTeams();
        if (live) setSavedTeams(res.teams);
      } catch {
        // Non-fatal
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  if (error) return (
    <div className="app-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" onClick={onBack} aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <span className="ds-appbar__body"><b className="ds-appbar__title">Tournament</b></span>
      </header>
      <div className="ds-hint ds-hint--error">{error}</div>
    </div>
  );

  if (!data) return (
    <div className="app-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" onClick={onBack} aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <span className="ds-appbar__body"><b className="ds-appbar__title">Tournament</b></span>
      </header>
      <div className="ds-caption">Loading tournament...</div>
    </div>
  );

  const { tournament, fixtures, teams } = data;
  const path = publicTournamentPath(tournament.shortcode);
  const hasFixtures = fixtures.length > 0;
  const tournamentTeamIds = new Set<string>(teams.map((t) => t.teamId));

  const addTeamToTournament = async (teamId: TeamId): Promise<void> => {
    setAddingTeamId(teamId);
    try {
      await api.addTournamentTeams(tournamentId, { teamIds: [teamId] });
      await loadTournament();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to add team");
    } finally {
      setAddingTeamId(null);
    }
  };

  const createAndAddTeam = async (): Promise<void> => {
    const name = newTeamName.trim();
    if (name === "") return;
    setCreatingTeam(true);
    try {
      const team = await api.createTeam({ name, playerIds: [] });
      setSavedTeams((prev) => (prev ? [...prev, team] : [team]));
      await api.addTournamentTeams(tournamentId, { teamIds: [team.id] });
      setNewTeamName("");
      await loadTournament();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to create team");
    } finally {
      setCreatingTeam(false);
    }
  };

  const generateFixtures = async (): Promise<void> => {
    setGenerating(true);
    try {
      await api.generateFixtures(tournamentId, { venues: [] });
      await loadTournament();
      setTab("fixtures");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to generate fixtures");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="app-page">
      <header className="ds-appbar">
        <button className="ds-iconbtn" onClick={onBack} aria-label="Back">
          <svg viewBox="0 0 24 24"><path d="m14 6-6 6 6 6" /></svg>
        </button>
        <span className="ds-appbar__body">
          <b className="ds-appbar__title">{tournament.name}</b>
        </span>
      </header>

      {/* Hero card */}
      <article className="ds-card" style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-4)" }}>
        <div className="row" style={{ marginBottom: "var(--ds-space-3)" }}>
          <span className="ds-iconslot ds-iconslot--amber" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" />
              <path d="M7 6H4.5v1.5A3.5 3.5 0 0 0 8 11" />
              <path d="M17 6h2.5v1.5A3.5 3.5 0 0 1 16 11" />
              <path d="M12 14v3" /><path d="M8.5 20h7" />
            </svg>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span className="ds-caption" style={{ display: "block" }}>
              {tournament.startDate} — {tournament.endDate}
            </span>
            <span className="ds-caption" style={{ display: "block" }}>
              {tournament.format === "round_robin" ? "Round Robin" : "Groups + Knockout"} · {tournament.ruleSet.name} · {teams.length} team{teams.length !== 1 ? "s" : ""}
            </span>
          </span>
        </div>
        <a className="ds-btn ds-btn--secondary ds-btn--sm" href={path} target="_blank" rel="noreferrer">
          Public page
        </a>
      </article>

      {/* Phase 1–2: No fixtures yet — add teams and generate */}
      {!hasFixtures ? (
        <>
          <AddTeamsSection
            teams={teams}
            savedTeams={savedTeams}
            tournamentTeamIds={tournamentTeamIds}
            addingTeamId={addingTeamId}
            newTeamName={newTeamName}
            creatingTeam={creatingTeam}
            generating={generating}
            playersPerSide={tournament.ruleSet.playersPerSide}
            onSetNewTeamName={setNewTeamName}
            onAddTeam={addTeamToTournament}
            onCreateAndAdd={createAndAddTeam}
            onGenerate={generateFixtures}
            onManagePlayers={(team) => setManagePlayersTeam(team)}
          />

          {managePlayersTeam && (
            <AddPlayerSheet
              teamId={managePlayersTeam.id}
              teamName={managePlayersTeam.name}
              existingPlayerIds={managePlayersTeam.squad.map((p) => p.id)}
              onPlayerAdded={() => {
                void loadTournament();
                void api.listTeams().then((r) => setSavedTeams(r.teams)).catch(() => {});
                setManagePlayersTeam(null);
              }}
              onClose={() => setManagePlayersTeam(null)}
            />
          )}
        </>
      ) : null}

      {/* Phase 3: Fixtures exist — tabs */}
      {hasFixtures ? (
        <>
          <div className="ds-segmented" role="tablist" style={{ width: "100%", marginBottom: "var(--ds-space-4)" }}>
            {(["fixtures", "standings", "leaderboards"] as const).map((t) => (
              <button
                key={t}
                className="ds-segmented__item"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                style={{ flex: 1 }}
              >
                {t[0]!.toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>

          {tab === "fixtures" ? <FixturesList fixtures={fixtures} onOpenMatch={onOpenMatch} /> : null}
          {tab === "standings" ? <StandingsTable standings={standings} /> : null}
          {tab === "leaderboards" ? (
            <div className="ds-card ds-card--flat" style={{ padding: "var(--ds-space-6)", textAlign: "center" }}>
              <div className="ds-caption">Leaderboards load after matches are scored.</div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

// ── Add-teams section ────────────────────────────────────────────────────────

interface AddTeamsSectionProps {
  readonly teams: readonly { readonly teamId: TeamId; readonly name: string }[];
  readonly savedTeams: readonly Team[] | null;
  readonly tournamentTeamIds: ReadonlySet<string>;
  readonly addingTeamId: string | null;
  readonly newTeamName: string;
  readonly creatingTeam: boolean;
  readonly generating: boolean;
  readonly playersPerSide: number;
  readonly onSetNewTeamName: (name: string) => void;
  readonly onAddTeam: (teamId: TeamId) => void;
  readonly onCreateAndAdd: () => void;
  readonly onGenerate: () => void;
  readonly onManagePlayers: (team: Team) => void;
}

function AddTeamsSection({
  teams,
  savedTeams,
  tournamentTeamIds,
  addingTeamId,
  newTeamName,
  creatingTeam,
  generating,
  playersPerSide,
  onSetNewTeamName,
  onAddTeam,
  onCreateAndAdd,
  onGenerate,
  onManagePlayers,
}: AddTeamsSectionProps): React.ReactElement {
  const hasEnough = teams.length >= 2;

  return (
    <>
      {/* Current tournament teams */}
      {teams.length > 0 ? (
        <div className="ds-card" style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-3)" }}>
          <div className="ds-label" style={{ marginBottom: "var(--ds-space-3)" }}>
            Teams in tournament ({playersPerSide}-a-side)
          </div>
          <div className="ds-list">
            {teams.map((t) => {
              const squad = savedTeams?.find((s) => s.id === t.teamId);
              const count = squad?.squad.length ?? 0;
              const short = count < playersPerSide;
              return (
                <div key={t.teamId} className="ds-list-item" style={{ cursor: "default" }}>
                  <span className="ds-list-item__body">
                    <b className="ds-list-item__title">{t.name}</b>
                    <span className="ds-list-item__meta">{short ? `${count}/${playersPerSide} players` : `${count} players`}</span>
                  </span>
                  {short ? (
                    <span className="ds-badge ds-badge--warning">needs {playersPerSide - count}</span>
                  ) : (
                    <span className="ds-badge ds-badge--success">ready</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Saved teams to add */}
      <div className="ds-card" style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-3)" }}>
        <div className="ds-label" style={{ marginBottom: "var(--ds-space-1)" }}>Add teams</div>
        <div className="ds-caption" style={{ marginBottom: "var(--ds-space-3)" }}>
          {teams.length} of 2 teams minimum · {playersPerSide} players required per team
          {hasEnough ? " — ready to generate fixtures" : ""}
        </div>

        {savedTeams === null ? (
          <div className="ds-caption">Loading your saved teams...</div>
        ) : savedTeams.length === 0 ? (
          <div className="ds-caption">No saved teams yet. Create one below.</div>
        ) : (
          <div className="ds-list">
            {savedTeams.map((team) => {
              const alreadyAdded = tournamentTeamIds.has(team.id);
              const isAdding = addingTeamId === team.id;
              const short = team.squad.length < playersPerSide;
              return (
                <button
                  key={team.id}
                  className="ds-list-item"
                  disabled={alreadyAdded || isAdding}
                  style={alreadyAdded ? { opacity: 0.5 } : undefined}
                  onClick={() => { if (!alreadyAdded) void onAddTeam(team.id as TeamId); }}
                >
                  <span className="ds-list-item__body">
                    <b className="ds-list-item__title">{team.name}</b>
                    <span className="ds-list-item__meta">
                      {alreadyAdded
                        ? "Already added"
                        : isAdding
                          ? "Adding..."
                          : `${team.squad.length}/${playersPerSide} players`}
                    </span>
                  </span>
                  {alreadyAdded && short ? (
                    <button
                      className="ds-btn ds-btn--secondary ds-btn--sm"
                      onClick={(e) => { e.stopPropagation(); onManagePlayers(team); }}
                    >
                      Add players
                    </button>
                  ) : !alreadyAdded && short ? (
                    <span className="ds-badge ds-badge--warning">needs {playersPerSide - team.squad.length}</span>
                  ) : alreadyAdded ? (
                    <span className="ds-badge ds-badge--success">added</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Create new team inline */}
      <div className="ds-card" style={{ padding: "var(--ds-space-4)", marginBottom: "var(--ds-space-3)" }}>
        <div className="ds-label" style={{ marginBottom: "var(--ds-space-3)" }}>Create new team</div>
        <div className="row" style={{ gap: "var(--ds-space-2)" }}>
          <input
            className="ds-input"
            type="text"
            placeholder="Team name"
            value={newTeamName}
            onChange={(e) => onSetNewTeamName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void onCreateAndAdd(); }}
            disabled={creatingTeam}
            style={{ flex: 1 }}
          />
          <button
            className="ds-btn ds-btn--primary ds-btn--sm"
            disabled={newTeamName.trim() === "" || creatingTeam}
            onClick={() => void onCreateAndAdd()}
          >
            {creatingTeam ? "Creating..." : "Create & Add"}
          </button>
        </div>
        <div className="ds-caption" style={{ marginTop: "var(--ds-space-2)" }}>
          After creating, tap "Add players" to build the squad.
        </div>
      </div>

      {/* Generate fixtures button */}
      {hasEnough ? (
        <button
          className="ds-btn ds-btn--primary ds-btn--block"
          disabled={generating}
          onClick={() => void onGenerate()}
          style={{ marginTop: "var(--ds-space-2)" }}
        >
          {generating ? "Generating fixtures..." : `Generate fixtures for ${teams.length} teams`}
        </button>
      ) : null}
    </>
  );
}

// ── Fixtures list ────────────────────────────────────────────────────────────

function statusBadge(status: Fixture["status"]): { label: string; cls: string } {
  switch (status) {
    case "live": return { label: "Live", cls: "ds-badge--success" };
    case "completed": return { label: "Completed", cls: "" };
    case "abandoned": return { label: "Abandoned", cls: "ds-badge--warning" };
    case "no_result": return { label: "No Result", cls: "ds-badge--warning" };
    default: return { label: "Upcoming", cls: "ds-badge--accent" };
  }
}

function FixturesList({
  fixtures,
  onOpenMatch,
}: {
  readonly fixtures: readonly Fixture[];
  readonly onOpenMatch: (matchId: string) => void;
}): React.ReactElement {
  if (fixtures.length === 0) {
    return (
      <div className="ds-card ds-card--flat" style={{ padding: "var(--ds-space-6)", textAlign: "center" }}>
        <div className="ds-caption">No fixtures generated yet.</div>
      </div>
    );
  }

  return (
    <div className="ds-list">
      {fixtures.map((fixture) => {
        const badge = statusBadge(fixture.status);
        return (
          <button
            key={fixture.matchNumber}
            className="ds-list-item"
            onClick={() => onOpenMatch(fixture.matchId)}
          >
            <span className="ds-list-item__body">
              <b className="ds-list-item__title">
                #{fixture.matchNumber} {fixture.teamAName ?? "TBD"} v {fixture.teamBName ?? "TBD"}
              </b>
              {fixture.group ? <span className="ds-list-item__meta">Group {fixture.group}</span> : null}
              {fixture.venue ? <span className="ds-list-item__meta">{fixture.venue}</span> : null}
              {fixture.resultText ? (
                <span className="ds-list-item__meta" style={{ fontWeight: 600, color: "var(--ds-success-600)" }}>
                  {fixture.resultText}
                </span>
              ) : null}
            </span>
            <span className={`ds-badge ${badge.cls}`}>{badge.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Standings table ──────────────────────────────────────────────────────────

function StandingsTable({ standings }: { readonly standings: readonly StandingsRow[] }): React.ReactElement {
  if (standings.length === 0) {
    return (
      <div className="ds-card ds-card--flat" style={{ padding: "var(--ds-space-6)", textAlign: "center" }}>
        <div className="ds-caption">Standings appear after matches are scored.</div>
      </div>
    );
  }

  return (
    <div className="ds-table-wrap">
      <table className="ds-table ds-table--compact">
        <thead>
          <tr>
            <th>Team</th>
            <th className="ds-num">P</th>
            <th className="ds-num">W</th>
            <th className="ds-num">L</th>
            <th className="ds-num">NR</th>
            <th className="ds-num">Pts</th>
            <th className="ds-num">NRR</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((row, i) => (
            <tr key={row.teamId} className={i === 0 ? "ds-row-lead" : undefined}>
              <td style={{ fontWeight: 600 }}>
                <span className="ds-pos">{i + 1}</span>
                {row.teamName}
              </td>
              <td className="ds-num">{row.played}</td>
              <td className="ds-num">{row.won}</td>
              <td className="ds-num">{row.lost}</td>
              <td className="ds-num">{row.noResult}</td>
              <td className="ds-num" style={{ fontWeight: 700 }}>{row.points}</td>
              <td className="ds-num">{row.nrr > 0 ? "+" : ""}{row.nrr.toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
